import { useCallback, useEffect, useState } from "react";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_MAX_ENTRIES = 200;

// Module-level so a tab that unmounts (and its siblings) can reuse a response
// when the user comes back to the same player + range.
const cache = new Map<string, { at: number; data: unknown }>();

export const peekApiCache = <T>(url: string): T | null => {
  const hit = cache.get(url);
  if (!hit) return null;
  if (Date.now() - hit.at > CACHE_TTL_MS) {
    cache.delete(url);
    return null;
  }
  return hit.data as T;
};

export const writeApiCache = (url: string, data: unknown) => {
  if (cache.size >= CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(url, { at: Date.now(), data });
};

type State<T> = {
  // Last response that arrived, and the scope (player) it belongs to, so a
  // range change can keep showing it dimmed while a new player can't.
  lastData: T | null;
  lastScope: string;
  doneUrl: string | null;
  error: ApiError | null;
};

export type ApiResource<T> = {
  data: T | null;
  // True while a request for the current url is in flight.
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
};

// GETs `url` (null disables it), caching the JSON per url. `scope` identifies
// what the data is about (the player); data from another scope is never
// returned.
export const useApiResource = <T>(
  url: string | null,
  scope: string
): ApiResource<T> => {
  const [state, setState] = useState<State<T>>({
    lastData: null,
    lastScope: scope,
    doneUrl: null,
    error: null
  });
  const [reloadCount, setReloadCount] = useState(0);

  const cached = url ? peekApiCache<T>(url) : null;

  useEffect(() => {
    if (!url || peekApiCache(url)) return undefined;
    const controller = new AbortController();

    const load = async () => {
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) {
          let message = `Request failed (${res.status})`;
          try {
            const body = await res.json();
            if (body && typeof body.error === "string") message = body.error;
          } catch {
            // Non-JSON error body; keep the generic message.
          }
          throw new ApiError(res.status, message);
        }
        const data = (await res.json()) as T;
        if (controller.signal.aborted) return;
        writeApiCache(url, data);
        setState({ lastData: data, lastScope: scope, doneUrl: url, error: null });
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Failed to fetch", url, err);
        const error =
          err instanceof ApiError
            ? err
            : new ApiError(0, "Couldn't reach the server");
        setState((prev) => ({
          ...prev,
          doneUrl: url,
          error
        }));
      }
    };
    load();

    return () => controller.abort();
  }, [url, scope, reloadCount]);

  const reload = useCallback(() => {
    if (url) cache.delete(url);
    setState((prev) => ({ ...prev, doneUrl: null, error: null }));
    setReloadCount((n) => n + 1);
  }, [url]);

  if (!url) return { data: null, loading: false, error: null, reload };

  if (cached) return { data: cached, loading: false, error: null, reload };

  const settled = state.doneUrl === url;
  return {
    data: state.lastScope === scope ? state.lastData : null,
    loading: !settled,
    error: settled ? state.error : null,
    reload
  };
};
